import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

import { cn } from "@/lib/utils";

const DARK_BACKGROUND = "transparent";
const AXIS_COLOR = "rgba(148,163,184,0.6)";
const SPLIT_COLOR = "rgba(148,163,184,0.18)";
const FOREGROUND = "#e2e8f0";

type EChartProps = {
  option: EChartsOption;
  height?: number | string;
  className?: string;
  notMerge?: boolean;
};

export function EChart({ option, height = 320, className, notMerge = true }: EChartProps): JSX.Element {
  const themed = useMemo<EChartsOption>(() => {
    const base: EChartsOption = {
      backgroundColor: DARK_BACKGROUND,
      textStyle: { color: FOREGROUND, fontFamily: "Inter, system-ui, sans-serif" },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#171718",
        borderColor: "#2d2d2f",
        textStyle: { color: FOREGROUND },
      },
      legend: { textStyle: { color: FOREGROUND }, top: 0 },
      grid: { left: 48, right: 24, top: 40, bottom: 32, containLabel: true },
    };
    const merged: EChartsOption = { ...base, ...option };
    if (option.xAxis !== undefined) {
      merged.xAxis = applyAxisTheme(option.xAxis) as EChartsOption["xAxis"];
    }
    if (option.yAxis !== undefined) {
      merged.yAxis = applyAxisTheme(option.yAxis) as EChartsOption["yAxis"];
    }
    return merged;
  }, [option]);

  return (
    <ReactECharts
      option={themed}
      notMerge={notMerge}
      lazyUpdate
      style={{ height, width: "100%" }}
      className={cn("rounded-md", className)}
      theme="dark"
    />
  );
}

type AxisLike = Record<string, unknown>;

function applyAxisTheme(axis: unknown): unknown {
  const themed: AxisLike = {
    axisLine: { lineStyle: { color: AXIS_COLOR } },
    axisLabel: { color: FOREGROUND },
    splitLine: { lineStyle: { color: SPLIT_COLOR } },
  };
  if (Array.isArray(axis)) {
    return axis.map((entry) => ({ ...themed, ...(entry as AxisLike) }));
  }
  return { ...themed, ...((axis as AxisLike) ?? {}) };
}
